import {
  validationCompanyNameRegex,
  validationNameRegex,
} from 'src/common/regex';
import { getFlowFormValidationRuleWithRequirement } from 'src/common/validation';
import SchemaTyped from 'rsuite/esm/Schema/Schema';
import { PersonOptionalFields } from 'src/types/configuration';
import { ERROR_EMAIL, ERROR_PHONE_FORMAT } from '../constants';

const { ObjectType, StringType } = SchemaTyped.Types;

export const LOCAL_CONTACT_ADDRESS_INVALID_MESSAGE =
  "Your Local Contact's physical address could not be validated. Please use the provided dropdown to select a valid address from the list.";

export const LOCAL_CONTACT_PROXIMITY_MESSAGE =
  'Your Local Contact must be able to attend the property within one hour to respond to any incidents that may occur. Please select a different Local Contact that lives closer to the property.';

export const SELECTED_CONTACT_PROXIMITY_MESSAGE =
  'Your physical address is more than 1 hour from the property. Please select a different local contact.';
export const getPropertyManagerValidationModel = (
  optionalFields: PersonOptionalFields
) => ({
  firstName: getFlowFormValidationRuleWithRequirement(
    'firstName',
    optionalFields,
    StringType().pattern(
      validationNameRegex,
      'Enter a first name using letters, spaces, or hyphens only'
    ),
    'Enter a first name'
  ),
  lastName: getFlowFormValidationRuleWithRequirement(
    'lastName',
    optionalFields,
    StringType().pattern(
      validationNameRegex,
      'Enter a last name using letters, spaces, or hyphens only'
    ),
    'Enter a last name'
  ),
  companyName: getFlowFormValidationRuleWithRequirement(
    'companyName',
    { companyName: 'optional' },
    StringType().pattern(
      validationCompanyNameRegex,
      'Company names must begin with ., @, & or an alphanumeric character.  The remainder of the name may only include -, !, ., @, &, (, ), alphanumeric characters or a single space character.'
    ),
    'Enter a company name'
  ),
  email: getFlowFormValidationRuleWithRequirement(
    'email',
    optionalFields,
    StringType().isEmail(ERROR_EMAIL),
    'Enter an email address'
  ),
  phone: getFlowFormValidationRuleWithRequirement(
    'phone',
    optionalFields,
    StringType().pattern(/^\d{10}$/, ERROR_PHONE_FORMAT),
    ERROR_PHONE_FORMAT
  ),
  mailingAddress: StringType().isRequired('Enter a mailing address'),
});

export const getOwnerRepValidationModel = (
  optionalFields: PersonOptionalFields
) => {
  const allowedZipCodes: number[] =
    optionalFields.physical_address?.allowed_postcodes || [];

  return {
    companyName: getFlowFormValidationRuleWithRequirement(
      'companyName',
      { companyName: 'optional' },
      StringType().pattern(
        validationCompanyNameRegex,
        'Company names must begin with ., @, & or an alphanumeric character.  The remainder of the name may only include -, !, ., @, &, (, ), alphanumeric characters or a single space character.'
      ),
      'Enter a company name'
    ),
    email: getFlowFormValidationRuleWithRequirement(
      'email',
      optionalFields,
      StringType().isEmail(ERROR_EMAIL),
      'Enter an email address'
    ),
    firstName: getFlowFormValidationRuleWithRequirement(
      'firstName',
      optionalFields,
      StringType().pattern(
        validationNameRegex,
        'Enter a first name using letters, spaces, or hyphens only'
      ),
      'Enter a first name'
    ),
    lastName: getFlowFormValidationRuleWithRequirement(
      'lastName',
      optionalFields,
      StringType().pattern(
        validationNameRegex,
        'Enter a last name using letters, spaces, or hyphens only'
      ),
      'Enter a last name'
    ),
    mailingAddress: StringType().isRequired('Enter a mailing address'),
    phone: getFlowFormValidationRuleWithRequirement(
      'phone',
      optionalFields,
      StringType().pattern(/^\d{10}$/, 'A 10 digit phone number is required'),
      'Enter a 10-digit phone number'
    ),
    ...(optionalFields.physical_address?.required && {
      completePhysicalAddress: ObjectType()
        .isRequired(LOCAL_CONTACT_ADDRESS_INVALID_MESSAGE)
        .addRule(
          (value) =>
            validateZipcodeProximity(
              (value as { components?: { zipcode?: string } })?.components
                ?.zipcode || '',
              allowedZipCodes
            ),

          LOCAL_CONTACT_PROXIMITY_MESSAGE
        ),
    }),
  };
};

const validateZipcodeProximity = (
  zipcode: string,
  allowedZipCodes: number[]
): boolean => {
  if (!allowedZipCodes.length) {
    return true;
  }
  return allowedZipCodes.includes(Number(zipcode));
};
